import React, { useEffect, useState } from "react";
import { FaLock, FaCheckCircle } from "react-icons/fa";
import axios from "axios";
import Apiurl from "../environment/Apiurl";

const Settings = () => {
  const localUserId = localStorage.getItem("user_id") || "";

  const [userRecord, setUserRecord] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      setLoadingUser(true);
      try {
        const response = await axios.get(`${Apiurl}/user`);
        const allUsers = response.data || [];
        const match = allUsers.find((u) => u.user_id === localUserId);
        setUserRecord(match || null);
      } catch (err) {
        console.error("Error fetching user for settings:", err);
      } finally {
        setLoadingUser(false);
      }
    };

    if (localUserId) {
      fetchUser();
    } else {
      setLoadingUser(false);
    }
  }, [localUserId]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!userRecord?._id) {
      setIsError(true);
      setMessage("Could not find your account record. Please re-login and try again.");
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      setIsError(true);
      setMessage("Please fill in all password fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setIsError(true);
      setMessage("New password and confirmation do not match.");
      return;
    }

    if (newPassword.length < 6) {
      setIsError(true);
      setMessage("New password must be at least 6 characters long.");
      return;
    }

    setSubmitting(true);
    try {
      // Verify current password via the login endpoint before changing it
      await axios.post(`${Apiurl}/login`, {
        user_id: localUserId,
        password: currentPassword,
      });

      // Send the full record back so we don't wipe out other fields,
      // only overriding the password.
      await axios.put(`${Apiurl}/user/${userRecord._id}`, {
        user_id: userRecord.user_id,
        username: userRecord.username,
        role_id: userRecord.role_id,
        user_type: userRecord.user_type,
        device_user_id: userRecord.device_user_id,
        status: userRecord.status,
        updated_by: localUserId,
        password: newPassword,
      });

      setIsError(false);
      setMessage("Password updated successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("Error changing password:", err);
      setIsError(true);
      if (err?.response?.status === 401) {
        setMessage("Current password is incorrect.");
      } else {
        setMessage(
          err?.response?.data?.message || "Failed to update password. Please try again."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-6">
          <div className="card border-0">
            <div className="card-body p-4 p-md-5">
              <h4 className="mb-1">Account Settings</h4>
              <p className="text-muted mb-4">Manage your account security.</p>

              <h6 className="d-flex align-items-center mb-3">
                <FaLock className="me-2 text-secondary" />
                Change Password
              </h6>

              {message && (
                <div
                  className={`alert py-2 ${
                    isError ? "alert-danger" : "alert-success"
                  } d-flex align-items-center`}
                >
                  {!isError && <FaCheckCircle className="me-2" />}
                  {message}
                </div>
              )}

              <form onSubmit={handleChangePassword}>
                <div className="mb-3">
                  <label className="form-label">Current Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    disabled={loadingUser || submitting}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={loadingUser || submitting}
                  />
                </div>
                <div className="mb-4">
                  <label className="form-label">Confirm New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loadingUser || submitting}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loadingUser || submitting}
                >
                  {submitting ? "Updating..." : "Update Password"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
